import {get_translation} from "svg-utils";
import global from "./global.js";
import Style from "./utils/Style.js";
import InputTextArea from "./ui/InputTextArea.js";

// const d3 = global.d3;
const log = global.log.getLogger("f_mtac.resources");
log.setLevel(log.levels.WARN);

let Resources = function () {
    const colors = [
        {name: "green", value: "#33b133"},
        {name: "orange", value: "#ff8c00"},
        {name: "red", value: "#e60000"},
        {name: "black", value: "#000"}
    ];
    const attachment_colors = [{name: "gray", value: "#828282"}];
    const color_by_name = Object.fromEntries(colors.map((color) => [color.name, color]));
    const attachment_color_by_name = Object.fromEntries(attachment_colors.map((color) => [color.name, color]));
    const color_by_value = Object.fromEntries(colors.map((color) => [color.value, color]));
    const style = new Style();

    // resource scale provided by menu (maximum value for GIS)
    let scale_setting = global.app.scale.wb / 150;
    log.debug(`Resources.ctor scale_setting = ${scale_setting}`);

    // dynamic resource scale, depending on the zoom for GIS
    let scale = global.app.scale.wb / 150,
        scale_factor = 1;
    log.debug(`Resources.ctor scale = ${scale}`);
    let resources_group = {};
    let mtac_resources_address = () => global.commissioning.uri.resources;
    this.update_scale = () => {
        window.document.documentElement.style.setProperty("--kr", `${scale}`);

        // calculate values once to reduce cpu load
        const f4 = ((4 * global.app.scale.wb) / 150) * scale_factor;
        const f2 = ((2 * global.app.scale.wb) / 150) * scale_factor;
        const f1 = (global.app.scale.wb / 150) * scale_factor;
        const f = scale * scale_factor;
        style.rule_set(
            "resources",
            `
            #resources .resources, #resources .attachment, #geolocation .geonode, #scene .scene {pointer-events: all;}
            #resources .resource:not(.dragged) > g, #resources .attachment:not(.dragged) > g, #geolocation .geonode:not(.dragged) > g { transform-origin: 100px 100px; transform: scale(${f}); }
            #resources .resource.dragged > g, #resources .attachment.dragged > g, #geolocation .geonode.dragged > g { transform-origin: 100px 100px; transform: scale(${f1}); }
            #geolocation .arrow-north { transform: scale(${f4});}
            #geolocation .projection-axis text { transform: scale(${f2});}
            #geolocation .projection-axis rect { transform: scale(${f2});}
            #frame #grid_scale_overlay text { transform: scale(${f2});}
            #frame #grid_scale_overlay rect { transform: scale(${f2});}
            #scene .distance { transform: scale(${scale_factor});}
            #scene .arrow_head { transform: scale(${f});}
            #scene .arrow_segment { transform: scale(${f});}
            #scene .handle_resize_group .handle_resize { transform: scale(${f});}
            #resources .lineTracking {  transform-origin: 100px 100px;  transform: scale(${f});}
            #scene .placeholder > g { transform-origin: 100px 100px; transform: scale(${f1});}
            #resources .placeholder > g { transform-origin: 100px 100px; transform: scale(${f1});}
            #notes .placeholder > g { transform-origin: 100px 100px; transform: scale(${f1});}
            `
        );

        global.configuration.app.resource_scale = scale;
        log.debug(`Resources.update_scale app.resource_scale = ${scale}`);
        global.configuration.app.resource_scale_setting = scale_setting;
        log.debug(`Resources.update_scale app.resource_scale_setting = ${scale_setting}`);
        global.configuration.commit_store("app");
    };

    /**
     * scale is effective scale of the resources
     * When the map is a picture, the scale = scale_setting.
     * When the map is a GIS, the scale max = scale_setting
     */
    Object.defineProperty(this, "scale", {
        set: (k) => {
            scale = k;
            this.update_scale();
            log.debug(`Resources.set.scale = ${k}`);
        },
        get: () => scale
    });

    /**
     * scale_setting is the resource scale initied by the the menu
     * When the map is a picture, the scale = scale_setting.
     * When the map is a GIS, the scale max = scale_setting
     */
    Object.defineProperty(this, "scale_setting", {
        set: (value) => {
            scale_setting = value;
            log.debug(`Resources.set.scale_setting = ${value}`);
        },
        get: () => scale_setting
    });
    Object.defineProperty(this, "scale_factor", {
        set: (k) => {
            scale_factor = k;
            this.update_scale();
        },
        get: () => scale_factor
    });

    /**
     * @brief Reset the resource sale taking into account if the map is a GIS and if the client is on line
     */
    this.scale_reset = () => {
        log.debug("Resources.scale_reset in");
        this.scale_setting = global.app.scale.wb / 150;
        if (global.scene.map.is_gis === true && global.server.network.state === "online") {
            global.view.ol_driver.update_resource_scale();
        } else {
            this.scale = global.resources.scale_setting;
            this.show_pictures();
        }
    };

    this.load = function () {
        return fetch(mtac_resources_address(), {cache: "reload"})
            .then((resources) => resources.text())
            .then((resources_text) => {
                return new DOMParser().parseFromString(resources_text, "application/xml");
            })
            .then((resources_xml) => {
                let resources_group_xml = resources_xml.querySelector("svg g");
                if (resources_group_xml === undefined) {
                    throw "no main svg group";
                }
                resources_group.name = resources_group_xml.getAttribute("data-resource-group");
                resources_group.category = {};
                for (let category_xml of resources_group_xml.childNodes) {
                    //log.debug("Resources.load category:", category_xml);
                    if (category_xml.nodeName !== "g") {
                        continue;
                    }
                    let category_name = category_xml.getAttribute("data-resource-category");
                    resources_group.category[category_name] = {};
                    for (let resource_xml of category_xml.childNodes) {
                        if (category_xml.nodeName !== "g") {
                            continue;
                        }
                        let resource_name = resource_xml.getAttribute("data-resource-name");
                        resources_group.category[category_name][resource_name] = resource_xml;
                    }
                }
            });
    };

    Object.defineProperty(this, "colors", {
        set: () => {},
        get: () => colors
    });

    Object.defineProperty(this, "attachment_colors", {
        set: () => {},
        get: () => attachment_colors
    });

    Object.defineProperty(this, "color_by_name", {
        set: () => {},
        get: () => color_by_name
    });

    Object.defineProperty(this, "attachment_color_by_name", {
        set: () => {},
        get: () => attachment_color_by_name
    });

    Object.defineProperty(this, "color_by_value", {
        set: () => {},
        get: () => color_by_value
    });

    this.color = (handle, color) => {
        var resource_color = handle.select("g.color");
        var {x, y, width, height} = handle.select("g.figure").node().getBBox();
        var circle = resource_color.select("circle.color");
        if (circle.size() === 0) {
            circle = resource_color
                .append("circle")
                .attr("class", "color")
                .attr("cx", x + Math.floor(width / 2))
                .attr("cy", y + Math.floor(height / 2))
                .attr("r", 87);
        }
        circle.attr("fill", color);
    };
    this.is_dragged = (handle) => {
        if (handle.select("g.node").classed("dragged")) {
            return;
        }
        handle.select("g.node").classed("dragged", true);
    };
    this.not_dragged = (handle) => {
        if (!handle.select("g.node").classed("dragged")) {
            return;
        }
        handle.select("g.node").classed("dragged", false);
    };
    /**
     * @brief If no name is given will return the actual name, otherwise create the HTML attached to the resource. At the same time we update the floater name
     * @param cash_dom node
     * @param string name
     * @returns
     */
    this.name = (node, name) => {
        let resource = node;
        let resource_id = node.attr("id");
        let floater = $(`#floater_${resource_id}`);
        let title = floater.find("text");
        let title_text = "";
        if (name === undefined) {
            let text_name = resource.select("g.name").select("text.name");
            if (text_name.size() > 0) {
                return text_name.text();
            }
            return;
        }
        if (name === "") {
            resource.select("g.name").html("");
            // TODO: Move all floaters code to floaters module
            // We check internationalization if no name
            if (!resource.select(".user_name text").empty()) {
                title_text = resource.select(".user_name text").html();
            } else {
                const resource_type = floater.find(".floater_player").data("media_type");
                console.log(resource_type);
                if (["audio", "image", "stream", "video"].includes(resource_type)) {
                    title_text = global.internationalization.text(`resource.Floater-${resource_type}-textholder`);
                }
            }
            title.text(title_text);
            return;
        }
        let resource_name = resource.select("g.name");
        let resource_position = resource.select("g.figure").node().getBBox();
        let text = resource_name.select("text.name");
        if (text.size() === 0) {
            text = resource_name.append("text").attr("class", "name").attr("x", 0).attr("y", 0).style("text-anchor", "middle").attr("font-family", "Roboto Condensed").attr("font-size", "30").attr("font-weight", "700").attr("fill", "#000");
        }
        const LH = 50;
        const LP = 10;
        text.text(name);
        let text_position = text.node().getBBox();
        text.attr("x", resource_position.x + resource_position.width / 2).attr("y", resource_position.y + resource_position.height + LH / 2 + 5);
        let rect = resource_name.select("rect.name");
        if (rect.size() === 0) {
            rect = resource_name.append("rect").attr("class", "name").attr("rx", 20).attr("fill", "#fff");
        }
        rect.attr("x", resource_position.x + resource_position.width / 2 - text_position.width / 2 - LP)
            .attr("y", resource_position.y + resource_position.height - 5)
            .attr("width", text_position.width + LP * 2)
            .attr("height", LH);
        resource_name.append(() => text.node());

        if (!resource.select(".user_name text").empty()) {
            title_text = resource.select(".user_name text").html();
        } else {
            title_text = resource.select(".name text").html();
        }
        title.text(title_text);
    };

    /**
     * @brief If no user_name is given will return the actual user_name, otherwise create the HTML attached to the resource. Update the floater at the same time
     * @param cash_dom node
     * @param string user_name
     * @returns
     */
    this.user_name = (node, user_name) => {
        let resource = node;
        let resource_id = node.attr("id");
        let floater = document.getElementById("floater_" + resource_id);
        let title = $(floater).find("text");
        let title_text = "";
        let resource_type = $(floater).find(".floater_player").data("media_type");
        if (user_name === undefined) {
            let text_name = resource.select("g.user_name").select("text.user_name");
            if (text_name.size() > 0) {
                return text_name.text();
            }
            return;
        }
        if (user_name === "") {
            resource.select("g.user_name").html("");
            if (!node.select(".name text").empty()) {
                title_text = node.select(".name text").html();
            } else {
                // We check internationalization if no user name
                if (resource_type == "stream") {
                    title_text = global.internationalization.text("resource.Floater-stream-textholder");
                } else if (resource_type == "image") {
                    title_text = global.internationalization.text("resource.Floater-image-textholder");
                } else if (resource_type == "video") {
                    title_text = global.internationalization.text("resource.Floater-video-textholder");
                } else if (resource_type == "audio") {
                    title_text = global.internationalization.text("resource.Floater-audio-textholder");
                }
            }
            title.text(title_text);
            return;
        }
        let resource_user_name = resource.select("g.user_name");
        // let resource_position = resource.select("g.figure").node().getBBox();
        let text = resource_user_name.select("text.user_name");
        if (text.size() === 0) {
            text = resource_user_name.append("text").attr("class", "user_name").attr("x", 0).attr("y", 0).attr("font-family", "Roboto Condensed").attr("font-size", "30").attr("font-weight", "500").attr("fill", "#fff").attr("x", 175).attr("y", 65);
        }

        const CR = 15;
        text.text(user_name);
        let text_position = text.node().getBBox();
        let rect = resource_user_name.select("rect.user_name");
        if (rect.size() === 0) {
            rect = resource_user_name.append("rect").attr("class", "user_name").attr("rx", 25).attr("fill", "#000").attr("x", 115).attr("y", 30).attr("height", 50);
        }
        rect.attr("width", text_position.width + 2 * CR + 85);
        let circle = resource_user_name.select("circle.user_name");
        if (circle.size() === 0) {
            circle = resource_user_name.append("circle").attr("class", "user_name").attr("r", CR).attr("cy", 55);
        }
        circle.attr("cx", 200 + text_position.width);
        resource_user_name.append(() => text.node());

        title_text = node.select(".user_name text").html();
        title.text(title_text);
    };

    /**
     * @brief Set the stream url of a resource and create visual indicator
     * @param cash_dom node
     * @param string stream_url
     * @returns
     */
    this.stream = (node, stream_url) => {
        let stream_node = node.select(".stream");
        stream_node.attr("data-stream_url", stream_url);
        // We create path directly, don't know how to add icon directly
        let stream_indicator = stream_node.append("path").attr("d", "M9,34l0,-5.5c0,-0.552,-0.448,-1,-1,-1l-18.222,0c-0.553,0,-1,0.448,-1,1l0,15.333c0,0.553,0.447,1,1,1l18.222,0c0.552,0,1,-0.447,1,-1l0,-5.5l0,0l5.778,5.778l0,-15.889l-5.778,5.778z");
        stream_indicator.attr("class", "stream_indicator").attr("fill", "white").style("scale", "1.5");
        let stream_indicator_dimension = stream_indicator.node().getBBox();
        stream_indicator.attr("x", -stream_indicator_dimension.width + 20);
        let rect = stream_node
            .append("rect")
            .attr("class", "stream_indicator")
            .attr("rx", 25)
            .attr("fill", "#000")
            .attr("x", -stream_indicator_dimension.width - 15)
            .attr("y", 25)
            .attr("height", 60);
        rect.attr("width", stream_indicator_dimension.width + 95);

        stream_node.append(() => stream_indicator.node());
    };

    /**
     * @brief Get the color of the geolocation status od a given node
     * @param {*} node
     * @returns the color (string) or null if not found
     */
    this.get_status_color = (node) => {
        let data_user = node.attr("data-user");
        if (data_user) {
            let circle = document.querySelector(`g.resource[data-user="${data_user}"] circle.user_name`);
            if (circle != null) {
                let styles = getComputedStyle(circle);
                let color = styles.fill;
                return color;
            }
        }
        return null;
    };

    this.geoloc_status = (msisdn, status) => {
        if (status === "on") {
            style.rule_set(msisdn, `g.resource[data-msisdn="${msisdn}"] circle.user_name { fill: #0f0; }`);
        } else {
            style.rule_set(msisdn, `g.resource[data-msisdn="${msisdn}"] circle.user_name { fill: #f00; }`);
        }
    };

    // @brief
    // Returns the coordinates of the resource in an array: [latitude, longitude]
    this.coordinates = (node) => {
        let resource = node;
        if (resource.attr("data-longitude") !== null && resource.attr("data-latitude") !== null) {
            // Linked to an Agnet device with known location
            return [resource.attr("data-latitude"), resource.attr("data-longitude")];
        } else {
            if (global.storage.selected_context.projection.project_invert !== null) {
                // Get coordinates from map projection
                let {x, y} = get_translation(resource.attr("transform"));
                // add 100 to pixel coordinates as it is done in get_object_translate in Users.js
                let [longitude, latitude] = global.storage.selected_context.projection.project_invert([x + 100, y + 100]);
                return [latitude, longitude];
            } else {
                return null;
            }
        }
    };

    this.request_name = (node, tip, error) => {
        log.debug("Resources.request_name node = ", node);
        if (tip === undefined || tip === null) {
            tip = global.internationalization.text("resource.Resource-name");
        }
        if (error === undefined || error === null) {
            error = global.internationalization.text("resource.Resource-name-shall-not-exceed-number-characters", {number: "36"});
        }
        let current_text = global.resources.name(node);
        if (current_text === undefined) {
            current_text = "";
        }
        return InputTextArea.interact({
            tip: tip,
            validators: {
                pattern: ".{1,36}"
            },
            error_message: error,
            current_text
        }).then((resource_name_text) => {
            if (resource_name_text !== null) {
                this.name(node, resource_name_text);
            }
            return resource_name_text;
        });
    };

    this.request_stream_url = (node, tip, error) => {
        if (tip === undefined || tip === null) {
            tip = global.internationalization.text("resource.Floater-stream-link");
        }
        let current_text = "";
        return InputTextArea.interact({
            tip: tip,
            validators: {},
            error_message: error,
            current_text
        }).then((stream_url) => {
            if (stream_url !== null && stream_url !== "") {
                this.stream(node, stream_url);
            }
            return stream_url;
        });
    };

    /**
     * @brief find a resource by its name. Parse the categories until finding the resource
     * @param {*} resource_name IN, the resource name to find
     * @returns the resource node or undefined
     */
    this.find_resource = (resource_name) => {
        let categories = Object.keys(resources_group.category);
        for (let category of categories) {
            let names = Object.keys(resources_group.category[category]);
            if (names.includes(resource_name)) {
                let node = resources_group.category[category][resource_name].cloneNode(true);
                node.setAttribute("transform", "");
                return node;
            }
        }
    };

    /**
     * @brief Hide the pictures and names of the all the resources and medias. the colored circle is not hidden
     */
    this.hide_pictures = () => {
        log.debug("Resources.hide_pictures in");
        let list = document.querySelectorAll("g#resources .figure");
        for (const elem of list) {
            elem.style.display = "none";
        }
        list = document.querySelectorAll("g#resources g.name");
        for (const elem of list) {
            elem.style.display = "none";
        }
        list = document.querySelectorAll("g#resources g.user_name");
        for (const elem of list) {
            elem.style.display = "none";
        }
        list = document.querySelectorAll("g#resources g.color");
        for (const elem of list) {
            let visibility = $(elem).attr("visibility");
            if (visibility == "hidden") {
                $(elem).attr("visibility", "visible");
            }
        }
    };

    /**
     * @brief Show the pictures and names of the all the resources and medias
     */
    this.show_pictures = () => {
        log.debug("Resources.show_pictures in");
        let list = document.querySelectorAll("g#resources .figure");
        for (const elem of list) {
            elem.style.display = "";
        }
        list = document.querySelectorAll("g#resources g.name");
        for (const elem of list) {
            elem.style.display = "";
        }
        list = document.querySelectorAll("g#resources g.user_name");
        for (const elem of list) {
            elem.style.display = "";
        }
        list = document.querySelectorAll("g#resources g.color");
        for (const elem of list) {
            let visibility = $(elem).attr("visibility");
            if (visibility == "visible") {
                $(elem).attr("visibility", "hidden");
            }
        }
    };

    this.show_picture = (object) => {
        log.debug("Resources.show_picture in");
        object.handle.select("g.figure").node().style.display = "";
        object.handle.select("g.name").node().style.display = "";
        object.handle.select("g.user_name").node().style.display = "";

        let $circle = $(object.handle.select("g.color").node());
        let visibility = $circle.attr("visibility");
        log.debug("Resources.show_picture", {visibility});
        if (visibility == "visible") {
            $circle.attr("visibility", "hidden");
        }
    };

    Object.defineProperty(this, "categories", {
        get: () => Object.keys(resources_group.category).filter((category_name) => category_name.charAt(0) !== "_"),
        set: () => {}
    });
    Object.defineProperty(this, "category", {
        get: () => resources_group.category,
        set: () => {}
    });
};

global.app.bootstrap.register_module({
    name: "resources",
    deps: ["app", "configuration"],
    init: async () => {
        global.resources = new Resources();
        log.debug(`Resources.register_module scale = ${global.resources.scale}`);
        log.debug(`Resources.register_module scale_setting = ${global.resources.scale_setting}`);

        // If scalings are saved in master configuration, get the values
        if (global.configuration.app.resource_scale_setting !== undefined && global.configuration.app.resource_scale !== undefined) {
            log.debug(`Resources.register_module configuration.app.resource_scale_setting = ${global.configuration.app.resource_scale_setting}`);
            log.debug(`Resources.register_module configuration.app.resource_scale = ${global.configuration.app.resource_scale}`);
            global.resources.scale_setting = global.configuration.app.resource_scale_setting;
            global.resources.scale = global.configuration.app.resource_scale;
        }
    }
});

var resources_loaded = null;

global.app.bootstrap.register_module({
    name: "resources_load",
    deps: ["resources", "server"],
    init: async () => {
        resources_loaded = global.resources.load();
    }
});

global.app.bootstrap.register_module({
    name: "resources_loaded",
    deps: ["resources_load"],
    init: async () => {
        await resources_loaded;
    }
});

export default Resources;
